import z from 'zod';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn } from '../common/utils';
import { createNumericTransformer, createTimestampTransformer } from '../common/sql';
import {
  CurrentQuestionDirectiveVerbSchema,
  CurrentQuestionScaleScoreSchema,
  CurrentQuestionRelationshipSchema,
  CurrentQuestionTopicSchema,
  CurrentQuestionTypeSchema,
  DBCurrentQuestionDirectiveVerbSchema,
  DBCurrentQuestionScaleScoreSchema,
  DBCurrentQuestionRelationshipSchema,
  DBCurrentQuestionTopicSchema,
  DBCurrentQuestionTypeSchema,
  DBQuestionDirectiveVerbSchema,
  DBQuestionScaleScoreSchema,
  DBQuestionRelationshipSchema,
  DBQuestionTopicAssignmentSchema,
  DBQuestionTypeAssignmentSchema,
  QuestionDirectiveVerbSchema,
  QuestionScaleScoreSchema,
  QuestionRelationshipSchema,
  QuestionTopicAssignmentSchema,
  QuestionTypeAssignmentSchema,
} from './schemas';

type AnyObject = z.ZodObject<any>;

/**
 * Every assignment table and every resolution view carries a nullable
 * `confidence` and a `created_at`, so one factory serves all twelve codecs.
 * `numericFields` names the further `numeric` columns a dimension adds.
 */
const createEnrichmentCodecConfig = <TClient extends AnyObject, TDB extends AnyObject>(params: {
  name: string;
  clientSchema: TClient;
  dbSchema: TDB;
  numericFields?: readonly string[];
}): TransformerConfig<TClient, TDB> => ({
  name: params.name,
  clientSchema: params.clientSchema,
  dbSchema: params.dbSchema,
  transformers: [
    createNumericTransformer<TClient, TDB>('confidence', { nullable: true }),
    ...(params.numericFields ?? []).map(field => createNumericTransformer<TClient, TDB>(field)),
    createTimestampTransformer<TClient, TDB>('createdAt'),
  ],
});

export const QuestionTopicAssignmentCodecConfig = createEnrichmentCodecConfig({
  name: 'QuestionTopicAssignment',
  clientSchema: QuestionTopicAssignmentSchema,
  dbSchema: DBQuestionTopicAssignmentSchema,
});

export const QuestionTypeAssignmentCodecConfig = createEnrichmentCodecConfig({
  name: 'QuestionTypeAssignment',
  clientSchema: QuestionTypeAssignmentSchema,
  dbSchema: DBQuestionTypeAssignmentSchema,
});

export const QuestionDirectiveVerbCodecConfig = createEnrichmentCodecConfig({
  name: 'QuestionDirectiveVerb',
  clientSchema: QuestionDirectiveVerbSchema,
  dbSchema: DBQuestionDirectiveVerbSchema,
});

export const QuestionScaleScoreCodecConfig = createEnrichmentCodecConfig({
  name: 'QuestionScaleScore',
  clientSchema: QuestionScaleScoreSchema,
  dbSchema: DBQuestionScaleScoreSchema,
  numericFields: ['value'],
});

export const QuestionRelationshipCodecConfig = createEnrichmentCodecConfig({
  name: 'QuestionRelationship',
  clientSchema: QuestionRelationshipSchema,
  dbSchema: DBQuestionRelationshipSchema,
});

export const CurrentQuestionTopicCodecConfig = createEnrichmentCodecConfig({
  name: 'CurrentQuestionTopic',
  clientSchema: CurrentQuestionTopicSchema,
  dbSchema: DBCurrentQuestionTopicSchema,
});

export const CurrentQuestionTypeCodecConfig = createEnrichmentCodecConfig({
  name: 'CurrentQuestionType',
  clientSchema: CurrentQuestionTypeSchema,
  dbSchema: DBCurrentQuestionTypeSchema,
});

export const CurrentQuestionDirectiveVerbCodecConfig = createEnrichmentCodecConfig({
  name: 'CurrentQuestionDirectiveVerb',
  clientSchema: CurrentQuestionDirectiveVerbSchema,
  dbSchema: DBCurrentQuestionDirectiveVerbSchema,
});

export const CurrentQuestionScaleScoreCodecConfig = createEnrichmentCodecConfig({
  name: 'CurrentQuestionScaleScore',
  clientSchema: CurrentQuestionScaleScoreSchema,
  dbSchema: DBCurrentQuestionScaleScoreSchema,
  numericFields: ['value'],
});

export const CurrentQuestionRelationshipCodecConfig = createEnrichmentCodecConfig({
  name: 'CurrentQuestionRelationship',
  clientSchema: CurrentQuestionRelationshipSchema,
  dbSchema: DBCurrentQuestionRelationshipSchema,
});

export const encodeQuestionTopicAssignmentToDb = createEncodeToDbFn(
  QuestionTopicAssignmentCodecConfig
);
export const decodeQuestionTopicAssignmentFromDb = createDecodeFromDbFn(
  QuestionTopicAssignmentCodecConfig
);
export const encodeQuestionTypeAssignmentToDb = createEncodeToDbFn(
  QuestionTypeAssignmentCodecConfig
);
export const decodeQuestionTypeAssignmentFromDb = createDecodeFromDbFn(
  QuestionTypeAssignmentCodecConfig
);
export const encodeQuestionDirectiveVerbToDb = createEncodeToDbFn(QuestionDirectiveVerbCodecConfig);
export const decodeQuestionDirectiveVerbFromDb = createDecodeFromDbFn(
  QuestionDirectiveVerbCodecConfig
);
export const encodeQuestionScaleScoreToDb = createEncodeToDbFn(QuestionScaleScoreCodecConfig);
export const decodeQuestionScaleScoreFromDb = createDecodeFromDbFn(QuestionScaleScoreCodecConfig);
export const encodeQuestionRelationshipToDb = createEncodeToDbFn(QuestionRelationshipCodecConfig);
export const decodeQuestionRelationshipFromDb = createDecodeFromDbFn(
  QuestionRelationshipCodecConfig
);

export const decodeCurrentQuestionTopicFromDb = createDecodeFromDbFn(
  CurrentQuestionTopicCodecConfig
);
export const decodeCurrentQuestionTypeFromDb = createDecodeFromDbFn(CurrentQuestionTypeCodecConfig);
export const decodeCurrentQuestionDirectiveVerbFromDb = createDecodeFromDbFn(
  CurrentQuestionDirectiveVerbCodecConfig
);
export const decodeCurrentQuestionScaleScoreFromDb = createDecodeFromDbFn(
  CurrentQuestionScaleScoreCodecConfig
);
export const decodeCurrentQuestionRelationshipFromDb = createDecodeFromDbFn(
  CurrentQuestionRelationshipCodecConfig
);
